// -----------------------------------------------------------------------------
//  LOCAL-FIRST: the one architectural decision this file exists to enforce
// -----------------------------------------------------------------------------
//
//  There are two ways to build an app that works offline, and only one of them
//  survives contact with a village.
//
//    THE TEMPTING WAY   try the server; if it fails, fall back to the phone.
//                       Two code paths for every operation. The offline one
//                       runs perhaps twice a month, on somebody else's phone,
//                       where nobody can see it. It is broken and no one knows.
//
//    THE WAY THIS WORKS every screen reads from the phone and writes to the
//                       phone. Always. There is no online path to get right,
//                       because there is no online path. The network is a
//                       background job that drains a queue and refreshes a
//                       cache, and it can fail all week without an officer
//                       noticing anything except a number in a banner.
//
//  The second is slightly more work to build and enormously more likely to
//  still be correct in six months, because the field path is the ONLY path and
//  it is exercised by every tap, in the office, all day.
// -----------------------------------------------------------------------------

import { supabase } from './supabase'
import {
  db, dexieOutbox, outboxCounts, cacheHouseholds, cacheVillages, cacheRefOptions,
  type LocalHousehold,
} from './db'
import { syncNow, type OutboxEntry, type RpcFn, type SyncResult } from './outbox'

const rpc: RpcFn = async (fn, args) => {
  const { error } = await supabase.rpc(fn, args)
  return error ? { error: { message: error.message, code: error.code } } : { error: null }
}

async function refreshSession(): Promise<boolean> {
  try {
    const { data, error } = await supabase.auth.refreshSession()
    return !error && !!data.session
  } catch {
    return false
  }
}

// -----------------------------------------------------------------------------
//  RUNNING A SYNC
// -----------------------------------------------------------------------------

export async function runSync(opts?: { includeRejected?: boolean }): Promise<SyncResult> {
  const result = await syncNow(dexieOutbox, rpc, { refreshSession, ...opts })
  await markSettledHouseholds()
  notify()
  return result
}

/**
 * A household stops being "waiting to send" when nothing about it is left in
 * the queue — not when its own entry goes, because an assessment queued after
 * it is still unsent and the officer should still see the mark.
 */
async function markSettledHouseholds() {
  const queued = new Set((await dexieOutbox.list()).map((e) => e.household_uid))
  const pending = await db.households.filter((h) => h.pending).toArray()
  const settled = pending.filter((h) => !queued.has(h.household_uid))
  if (settled.length === 0) return
  await db.households.bulkPut(settled.map((h) => ({ ...h, pending: false })))
}

// -----------------------------------------------------------------------------
//  QUEUEING
// -----------------------------------------------------------------------------

/**
 * Put an operation in the queue and try to send it straight away.
 *
 * The send is deliberately NOT awaited by callers that care about the officer's
 * time. Saving is finished the moment the entry is on disk; whether the server
 * has heard about it is a separate question with a separate answer in the
 * banner. Making Save wait for the network is how an app that "supports
 * offline" still makes people stand in a doorway watching a spinner.
 */
export async function queue(entry: Omit<OutboxEntry, 'seq'>) {
  await dexieOutbox.add(entry)
  notify()
  if (navigator.onLine) void runSync().catch(() => {})
}

// -----------------------------------------------------------------------------
//  TELLING THE REST OF THE APP THAT SOMETHING CHANGED
// -----------------------------------------------------------------------------

type Listener = () => void
const listeners = new Set<Listener>()
function notify() {
  for (const l of listeners) l()
}
export function onOutboxChange(l: Listener): () => void {
  listeners.add(l)
  return () => listeners.delete(l)
}

export async function counts() {
  return outboxCounts()
}

// -----------------------------------------------------------------------------
//  WHEN TO TRY
// -----------------------------------------------------------------------------
//  navigator.onLine is not a promise that the internet works — on a phone it
//  frequently means "connected to a mobile network that is not passing any
//  traffic". It is a useful hint for when to TRY, never a reason to believe a
//  send will succeed. All the real work of deciding is in the outbox's error
//  classification.
// -----------------------------------------------------------------------------

let started = false

export function startAutoSync() {
  if (started) return
  started = true

  const attempt = () => {
    if (navigator.onLine) void runSync().catch(() => {})
  }

  window.addEventListener('online', attempt)

  // Coming back to the app is the moment an officer most wants the queue to
  // move — they have usually just walked somewhere with signal.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') attempt()
  })

  // A slow heartbeat. Not aggressive: a phone in a village will fail this
  // every two minutes all day, and each failure costs a radio wake-up.
  setInterval(attempt, 120_000)

  attempt()
}

// -----------------------------------------------------------------------------
//  FILLING THE CACHE
// -----------------------------------------------------------------------------
//  Called whenever there is signal. The officer never has to think about it:
//  by the time they reach the village the phone already holds everything,
//  because it topped itself up on the office wifi.
//
//  Every one of these returns quietly on failure. A refresh that cannot reach
//  the server is not an error — it is Tuesday.
// -----------------------------------------------------------------------------

export async function refreshVillages(): Promise<boolean> {
  const { data, error } = await supabase
    .from('v_village_quality_summary')
    .select('village_id, village_code, village_name_en, estimated_households, registered_active, coverage_pct, has_centroid, centroid_source')
    .order('village_code')
  if (error || !data) return false

  await cacheVillages(
    data.map((v: Record<string, unknown>) => ({
      village_id: v.village_id as string,
      code: v.village_code as string,
      name_en: (v.village_name_en as string) ?? '',
      name_km: null,
      estimated_households: (v.estimated_households as number) ?? null,
    })),
  )
  await db.meta.put({ key: 'villages_raw', value: data })
  await db.meta.put({ key: 'villages_at', value: new Date().toISOString() })
  return true
}

const HOUSEHOLD_COLUMNS =
  'household_uid, village_id, household_code, head_name, head_sex, household_size,' +
  ' adults_male_18_59, adults_female_18_59, children_under_18, elderly_60_plus,' +
  ' members_with_disability, record_status, has_latrine, latrine_type, water_source,' +
  ' lat, lon, gps_accuracy_m, gps_captured_at, last_assessed_at, updated_at'

export async function refreshHouseholds(villageId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('v_households')
    .select(HOUSEHOLD_COLUMNS)
    .eq('village_id', villageId)
    .neq('record_status', 'merged')
    .limit(2000)
  if (error || !data) return false

  await cacheHouseholds(villageId, data as unknown as Omit<LocalHousehold, 'pending'>[])
  await db.meta.put({ key: `households_at:${villageId}`, value: new Date().toISOString() })
  return true
}

export async function refreshRefOptions(): Promise<boolean> {
  const { data, error } = await supabase
    .from('v_ref_options')
    .select('list_code, code, label_en, label_km')
  if (error || !data) return false
  await cacheRefOptions(data as never)
  return true
}

/** Everything the phone needs to work in a village, fetched while there is signal. */
export async function refreshAll(villageId?: string) {
  if (!navigator.onLine) return
  await Promise.all([
    refreshVillages().catch(() => false),
    refreshRefOptions().catch(() => false),
    villageId ? refreshHouseholds(villageId).catch(() => false) : Promise.resolve(false),
  ])
}

export async function lastSyncedAt(key: string): Promise<string | null> {
  return ((await db.meta.get(key))?.value as string) ?? null
}
